import { Entries, PartialDeep } from 'type-fest';

import { ObjectiveCompletedState, ObjectiveGroupConfigStateCensored } from './objective.js';
import { TeamState, CensoredTeamState, PrunedTeamState, AnyTeamState, Team } from './team.js';
import { AuditScoreState } from './audit-score.js';
import {
    TrainingSessionTransportState,
    TrainingSessionStore
} from './training-session-transport.js';
import { CompletionState } from './objectives.js';
import { WorkshopEnvironment } from './workshop-environment.js';
import { TeamId } from './team-id.js';
import { MaybeDeleted as MaybeDeleted, isDeleted, Deleted } from './deleted.js';
import { arrayCompare, concatLatestFrom, deepApply, groupBy, objectCompare } from './util.js';

export interface ProgressScore {
    objectives: number;
    audit: number;
    bonus: number;
    penalty: number[];
    total: number;
}

export function copyObjectiveAchievement(input: ObjectiveAchievement): ObjectiveAchievement {
    const copiedPenalties =
        'penalty' in input ? { penalty: input.penalty && input.penalty.slice() } : {};
    return Object.assign({}, input, copiedPenalties);
}

export function calculateTotalScore(o: ObjectiveAchievement) {
    const { score, bonus, penalty: penaltyArray } = o;
    const penaltySum = penaltyArray ? penaltyArray.reduce((sum, p) => sum + p, 0) : 0;
    const bonusAmount = bonus;
    return score + bonusAmount + penaltySum;
}

export interface ObjectiveAchievement extends ObjectiveCompletedState {
    id: string;
    group: string;
    name: string;
    /**
     * Score this team has achieved for this objective
     */
    score: number;
    /**
     * Number of possible points
     */
    points: number;
    bonus: number;
    bonusIndex: number | null;
    optional: boolean;
    penalty?: number[];
    icon?: string;
    pregame?: boolean;
    unicode?: string;
    dependsOn?: string | null;
    answer?: string;
    /**
     * Multiple-choice options offered for this question (when the question is
     * a multiple-choice question). The correct answer is not included here.
     */
    choices?: string[];
}

export function objectiveAchievementCompletionState(oa: ObjectiveAchievement): CompletionState {
    return { completed: oa.completed, firstCompleted: oa.firstCompleted };
}

export function objectiveAchievementsEqual(a: ObjectiveAchievement, b: ObjectiveAchievement) {
    // compare the union of both items' keys so that a key present on only one
    // side (e.g. 'choices' when a question changes type) is detected as a change
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
        const v = a[k as keyof ObjectiveAchievement] as unknown;
        const w = b[k as keyof ObjectiveAchievement] as unknown;
        if ((k === 'penalty' || k === 'choices') && Array.isArray(v) && Array.isArray(w)) {
            if (!arrayCompare(v as string[], w as string[])) {
                return false;
            }
        } else if (v !== w) {
            return false;
        }
    }
    return true;
}

export interface TeamPenaltyBrief {
    points: number;
    reason: string;
}

export function teamPenaltyBriefToString(tp: TeamPenaltyBrief) {
    return `${tp.points}: ${tp.reason}`;
}

export interface ObjectiveGroupProgressState extends ObjectiveGroupConfigStateCensored {
    current: boolean;
    visible: boolean;
    // @typescript-to-gdscript-type: int
    completed: number; // timestamp
    // @typescript-to-gdscript-type: int
    totalPoints: number;
    // @typescript-to-gdscript-type: int
    totalScore: number;
    hasBonus: number;
    hasPenalty: boolean;
    hasPregame: boolean;
    hasRegression: boolean;
}

export function objectiveGroupProgressStatesEqual(
    a: ObjectiveGroupProgressState,
    b: ObjectiveGroupProgressState
) {
    const optionalSame =
        a.optionalCompletion && b.optionalCompletion
            ? objectCompare(a.optionalCompletion, b.optionalCompletion)
            : !!a.optionalCompletion == !!b.optionalCompletion;
    return (
        (Object.entries(a) as Entries<typeof a>).every(([k, v]) =>
            k === 'optionalCompletion' ? true : b[k] === v
        ) && optionalSame
    );
}

/**
 * Basic version of team progress store because groups, objectives and answers are all very big.
 * When sending one of these to the fndn api, for example it would be overkill to send all this
 * data every 10 minutes.
 */
export interface BasicTeamProgressStore {
    name: string;
    members: string[];
    score: ProgressScore;
    auditScore: AuditScoreState;
    teamPenalties: TeamPenaltyBrief[];
    // @typescript-to-gdscript-type: int
    rank?: number;
}

// @typescript-to-gdscript-skip
export interface TeamProgressStore extends BasicTeamProgressStore {
    groups: ObjectiveGroupProgressState[];
    objectives: ObjectiveAchievement[];
    answers: ObjectiveAchievement[];
}

/**
 * This is a sparse substitute for TeamProgressStore...
 * every property can be omitted if the value hasn't changed.
 * groups, objectives and answers can be sparse arrays containing only updated/deleted values
 */
export interface TeamProgressSparse extends PartialDeep<BasicTeamProgressStore> {
    // @typescript-to-gdscript-type: MaybeDeletedObjectiveGroupProgressState
    groups?: Array<MaybeDeleted<ObjectiveGroupProgressState>>;
    // @typescript-to-gdscript-type: MaybeDeletedObjectiveAchievmeent
    objectives?: Array<MaybeDeleted<ObjectiveAchievement>>;
    // @typescript-to-gdscript-type: MaybeDeletedObjectiveAchievement
    answers?: Array<MaybeDeleted<ObjectiveAchievement>>;
}

// @typescript-to-gdscript-skip
export interface TeamProgressWithEnvironment extends TeamProgressStore {
    environment: WorkshopEnvironment;
}

// @typescript-to-gdscript-skip
export interface TeamProgressResponse<T extends TeamAndProgress<any> | BasicTeamAndProgress> {
    teamsProgress: T[];
}

export interface BasicTeamAndProgress {
    team: CensoredTeamState;
    progress: BasicTeamProgressStore;
}

// @typescript-to-gdscript-skip
export interface TeamAndProgress<T extends AnyTeamState> {
    team: T;
    progress: TeamProgressStore;
}

// @typescript-to-gdscript-skip
export interface TeamAndPrunedProgress<T extends AnyTeamState>
    extends Omit<TeamAndProgress<T>, 'progress'> {
    progress: PrunedTeamProgressStore;
}

// @typescript-to-gdscript-skip
export type PartialDeepTeamState = PartialDeep<PrunedTeamState>;

// @typescript-to-gdscript-skip
export interface TeamAndProgressSparse {
    id: TeamId;
    team?: PartialDeepTeamState;
    progress: TeamProgressSparse;
}

// @typescript-to-gdscript-skip
export interface TrainingSessionAndProgressTransport<T extends TeamState | CensoredTeamState> {
    session: TrainingSessionTransportState;
    progress: Array<TeamAndProgress<T>>;
}

// @typescript-to-gdscript-skip
export type PrunedObjectiveGroupProgressState = Omit<
    ObjectiveGroupProgressState,
    'details' | 'name' | 'location' | 'locationText' | 'key'
>;
export type PrunedObjectiveAchievement = Omit<ObjectiveAchievement, 'details' | 'name' | 'key'>;

// @typescript-to-gdscript-skip
export interface PrunedTeamProgressStore
    extends Omit<TeamProgressStore, 'groups' | 'objectives' | 'answers'> {
    groups: PrunedObjectiveGroupProgressState[];
    objectives: PrunedObjectiveAchievement[];
    answers: PrunedObjectiveAchievement[];
}

// @typescript-to-gdscript-skip
export interface PrunedTeamProgressSparse
    extends Omit<TeamProgressStore, 'groups' | 'objectives' | 'answers'> {
    groups?: MaybeDeleted<PrunedObjectiveGroupProgressState>[];
    objectives?: MaybeDeleted<PrunedObjectiveAchievement>[];
    answers?: MaybeDeleted<PrunedObjectiveAchievement>[];
}

// @typescript-to-gdscript-skip
export interface TrainingSessionAndProgress<T extends TeamState | CensoredTeamState> {
    session: TrainingSessionStore;
    progress: Array<TeamAndPrunedProgress<T>>;
}

// @typescript-to-gdscript-skip
export type CensoredTrainingSessionAndProgressTransport =
    TrainingSessionAndProgressTransport<CensoredTeamState>;

// @typescript-to-gdscript-skip
export type CensoredTrainingSessionAndProgress = TrainingSessionAndProgress<CensoredTeamState>;

// @typescript-to-gdscript-skip
export interface PrunedTeamAndProgress {
    team: Readonly<CensoredTeamState | PrunedTeamState>;
    progress: Readonly<PrunedTeamProgressStore>;
}

// @typescript-to-gdscript-skip
export interface PrunedTeamAndProgressSparse extends Omit<TeamAndProgressSparse, 'progress'> {
    progress: PrunedTeamProgressSparse;
}

export function pruneProgress(progress: TeamProgressStore): Readonly<PrunedTeamProgressStore>;
export function pruneProgress(progress: TeamProgressSparse): Readonly<PrunedTeamProgressSparse>;
export function pruneProgress(
    progress: TeamProgressStore | TeamProgressSparse
): Readonly<PrunedTeamProgressStore | PrunedTeamProgressSparse> {
    const result = {
        ...progress
    } as PrunedTeamProgressStore | PrunedTeamProgressSparse;
    if (progress.groups) {
        result.groups = progress.groups.map(g => {
            if (isDeleted(g)) {
                return g;
            }
            const { details: _, name: __, location: ___, locationText: ____, ...result } = g;
            return result;
        });
    }
    if (progress.objectives) {
        result.objectives = progress.objectives.map(o => {
            if (isDeleted(o)) {
                return o;
            }
            const { details: _, name: __, key: ___, ...result } = o;
            return result;
        });
    }
    if (progress.answers) {
        result.answers = progress.answers.map(a => {
            if (isDeleted(a)) {
                return a;
            }
            const { details: _, name: __, key: ___, ...result } = a;
            return result;
        });
    }
    return result;
}

export function pruneTeamProgress(
    tps: Array<TeamAndProgress<CensoredTeamState | TeamState> | MaybeDeleted<TeamAndProgressSparse>>
): Array<PrunedTeamAndProgress | MaybeDeleted<PrunedTeamAndProgressSparse>> {
    return tps.map(tp => {
        if (isDeleted(tp)) {
            return tp;
        }
        if ('id' in tp) {
            return {
                id: tp.id,
                ...(tp.team ? { team: Team.pruneState(tp.team) } : {}),
                progress: pruneProgress(tp.progress)
            } as PrunedTeamAndProgressSparse;
        } else {
            return {
                team: Team.pruneState(tp.team),
                progress: pruneProgress(tp.progress)
            } as PrunedTeamAndProgress;
        }
    });
}

// @typescript-to-gdscript-skip
export type TeamAndProgressTransport =
    | TeamAndProgress<CensoredTeamState | PrunedTeamState>
    | TeamAndProgressSparse
    | Deleted<TeamId>;

export function updateTeamAndProgressMap(
    teamMap: Map<TeamId, TeamAndProgress<CensoredTeamState | PrunedTeamState>>,
    progress: TeamAndProgressTransport[]
): void {
    for (const tp of progress) {
        if (isDeleted(tp)) {
            teamMap.delete(tp.id);
        } else {
            const id = 'id' in tp ? tp.id : tp.team.id;
            const prev = teamMap.get(id);
            if (!prev) {
                if ('id' in tp) {
                    throw new Error('A non-sparse update for team progress was not found');
                } else {
                    teamMap.set(id, tp as TeamAndProgress<CensoredTeamState | PrunedTeamState>);
                }
            } else {
                teamMap.set(id, mergeTeamAndProgressSparse(prev, tp, false));
            }
        }
    }
}

export function teamAndProgressRankSort(
    a: TeamAndProgress<AnyTeamState>,
    b: TeamAndProgress<AnyTeamState>
) {
    return (a.progress.rank || 0) - (b.progress.rank || 0);
}

export function mergeTeamAndProgressArraySparse<
    TP extends TeamAndProgress<CensoredTeamState> | TeamAndProgressSparse,
    TPD extends TP | MaybeDeleted<TeamAndProgressSparse, TeamId>
>(prevTps: TPD[], nextTps: TPD[]): TPD[] {
    const result: TPD[] = [];
    const grouped = groupBy([...prevTps, ...nextTps], tp => ('id' in tp ? tp.id : tp.team.id));
    // this will reorder the array to group progresses for the same team together, but that's fine
    for (const tps of grouped.values()) {
        let last: TP | null = null;
        for (const tp of tps) {
            // deleted entries erase previous values..
            if (isDeleted(tp)) {
                last = null;
                result.push(tp);
            } else if (last) {
                last = mergeTeamAndProgressSparse(last, tp);
            } else {
                last = tp as TP;
            }
        }
        if (last) {
            result.push(last as TPD);
        }
    }
    return result;
}

export function mergeTeamAndProgressSparse(
    prev: TeamAndProgressSparse,
    next: TeamAndProgressSparse
): TeamAndProgressSparse;
export function mergeTeamAndProgressSparse<
    TP extends PrunedTeamState | CensoredTeamState,
    T extends TeamAndProgress<TP>
>(prev: T, next: T | TeamAndProgressSparse, retainDeleted: false): T;
export function mergeTeamAndProgressSparse<
    TP extends PrunedTeamState | CensoredTeamState,
    T extends TeamAndProgress<TP> | TeamAndProgressSparse
>(prev: T, next: T | MaybeDeleted<TeamAndProgressSparse>): T;
export function mergeTeamAndProgressSparse<
    TP extends PrunedTeamState | CensoredTeamState,
    T extends TeamAndProgress<TP> | TeamAndProgressSparse
>(
    prev: T | MaybeDeleted<TeamAndProgressSparse>,
    next: T,
    retainDeleted = true
): T | MaybeDeleted<TeamAndProgressSparse> {
    if ((isDeleted(prev) || !notSparse(prev)) && !retainDeleted) {
        throw new Error('Prev must not be deleted or sparse when called with retainDeleted: false');
    }
    if (isDeleted(prev) || isDeleted(next)) {
        return next;
    } else {
        if (notSparse(next)) {
            // if the next one is not sparse, we can throw away any previous sparse values.
            return next;
        } else {
            const prevNotSparse = notSparse(prev);
            // otherwise just merge. the two, {...notSparse, ...sparse} = notSparse
            const { id: _, ...nextNoId } = next;

            return {
                ...prev,
                // because the 'not sparse' object must not have the 'id' property
                // we must delete the 'id' property from next when prev is not sparse.
                // The type system isn't that flexible, so we need to cast and pretend that
                // next is always sparse...
                ...(prevNotSparse ? (nextNoId as Omit<TeamAndProgressSparse, 'id'>) : next),
                ...(next.team && prev.team ? { team: deepApply(prev.team, next.team) } : {}),
                progress: mergeTeamProgressSparse(
                    prev.progress,
                    next.progress,
                    prevNotSparse,
                    retainDeleted
                )
            };
        }
    }

    /**
     * Check if the TeamAndProgress is not sparse. If not then we can treat it as a 'whole' progress update.
     * @param tp An object which may or may not be sparse team and progress
     * @returns true when tp is not sparse.
     */
    function notSparse(
        tp: TeamAndProgress<CensoredTeamState> | TeamAndProgressSparse
    ): tp is TeamAndProgress<CensoredTeamState> {
        return !('id' in tp);
    }
}

export function mergeTeamProgressSparse<T extends TeamProgressSparse>(
    prev: T,
    next: TeamProgressSparse
): T;
export function mergeTeamProgressSparse<T extends TeamProgressSparse>(
    prev: T,
    next: TeamProgressSparse,
    prevNotSparse: boolean,
    retainDeleted: boolean
): T;
export function mergeTeamProgressSparse<T extends TeamProgressStore | TeamProgressSparse>(
    prev: T,
    next: TeamProgressSparse,
    prevNotSparse = false,
    retainDeleted = true
): T {
    const deletedGroups = new Set(
        retainDeleted || !next.groups ? [] : next.groups.filter(g => isDeleted(g)).map(d => d.id)
    );
    const deletedObjectives = new Set(
        retainDeleted || !next.objectives
            ? []
            : next.objectives.filter(o => isDeleted(o)).map(d => d.id)
    );
    const deletedAnswers = new Set(
        retainDeleted || !next.answers ? [] : next.answers.filter(a => isDeleted(a)).map(d => d.id)
    );
    return {
        ...deepApply(prev, next),
        groups: concatLatestFrom([prev, next], p => p.groups, uniqueId, prevNotSparse)?.filter(
            g => !deletedGroups.has(g.id)
        ),
        objectives: concatLatestFrom(
            [prev, next],
            p => p.objectives,
            uniqueId,
            prevNotSparse
        )?.filter(o => !deletedObjectives.has(o.id)),
        answers: concatLatestFrom([prev, next], p => p.answers, uniqueId, prevNotSparse)?.filter(
            a => !deletedAnswers.has(a.id)
        )
    };

    function uniqueId(value: { id: string } | Deleted<string>) {
        return isDeleted(value) ? `deleted:${value.id}` : value.id;
    }
}

export const EMPTY_PRUNED_TEAM_AND_PROGRESS = Object.freeze({
    team: {
        id: 0,
        name: '',
        expectedMemberCount: 0,
        members: [],
        auditScore: {
            passed: 0,
            low: 0,
            medium: 0,
            high: 0,
            score: 0
        },
        fortigateSn: '',
        fortigates: [],
        fortigateIp: '',
        objectives: {},
        startedObjectiveGroups: {},
        answers: {},
        previousScore: 0,
        code: '',
        instanceHeartbeat: null,
        rrWsConnected: '',
        rrWsDisconnected: '',
        restartRequestedAt: '',
        restartAckedAt: '',
        restartCompletedAt: '',
        countries: [],
        webhookObjectives: {},
        teamNotifications: [],
        securityReportObjectives: {},
        deviceStatuses: [],
        webhookResponses: {},
        collectedClues: []
    },
    progress: {
        groups: [],
        objectives: [],
        answers: [],
        name: '',
        members: [],
        score: {
            objectives: 0,
            audit: 0,
            bonus: 0,
            penalty: [],
            total: 0
        },
        auditScore: {
            passed: 0,
            low: 0,
            medium: 0,
            high: 0,
            score: 0
        },
        teamPenalties: []
    }
} satisfies TeamAndProgress<PrunedTeamState>);
